import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { AuthService } from "../api/auth.service";
import { LoginGuardService } from "../api/login-guard.service";
import { RoutesService } from "../api/routes.service";
import { AssessmentLockService } from "../core/assessmentlock.service";
import { LangService } from "../core/lang.service";
import { CALC_FRAME_ID } from "../ui-testrunner/widget-calculator/widget-calculator.component";
import { IPasteMismatchEvent, ISoftlockControls, IStudentPositionUpdate, StudentG9ConnectionService } from "./student-g9-connection.service";

// auto-pause mode uses the test ctrl rules to pause students automatically
export const SOFTLOCK_MODE_AUTO_PAUSE = 'softlock_auto_pause';

// legacy mode keeps the older softlock flow and skips these new rules
export const SOFTLOCK_MODE_LEGACY = 'softlock_legacy';

type SoftLockMode = typeof SOFTLOCK_MODE_AUTO_PAUSE | typeof SOFTLOCK_MODE_LEGACY;
export type SoftLockPauseCause = 'REENTRY' | 'THRESHOLD_LONG_AWAY' | 'THRESHOLD_SHORT_AWAY_COUNT' | 'PAGE_VISIBILITY_CHANGE' | 'WINDOW_BLUR_DETECTED' | 'WINDOW_FOCUS_DETECTED' | 'LEAVE_FULLSCREEN_CONFIRM' | 'FORCE_FULLSCREEN_ERR' | 'PASTE_MISMATCH';

export interface IAttemptContextInit {
  uid: number,
  test_attempt_id: number,
  test_session_id: number,
  is_soft_lock_enabled?: number | boolean,
  is_soft_lock_disabled?: number | boolean,
  is_paused?: number | boolean,
  isPreviouslyOpened?: boolean,
  isSebMode: boolean,
  softlockMode?: SoftLockMode,
  softlockControls?: ISoftlockControls,
  currentStudentPosition: {
    stageIndex: null | number,
    questionCaption: null | string,
    subSessionSlug?: null | string,
    subSessionCaption?: null | string,
  },
}

interface IAttemptContext extends IAttemptContextInit {
  numTimesOffScreen: number,
  is_paused: boolean,
  timeAwayStartedOn: number | null,
  timeAwayTotalMs: number,
  timeAwayMostRecentMs: number,
  awayOverOneSecondCount: number,
  awayOneToFiveSecondCount: number,
  awayUnderOneSecondCount: number,
  copyPasteMismatchCount: number,
}

interface IPersistedSoftLockMetrics {
  uid: number,
  test_attempt_id: number,
  test_session_id: number,
  numTimesOffScreen: number,
  timeAwayTotalMs: number,
  timeAwayMostRecentMs: number,
  awayOverOneSecondCount: number,
  awayOneToFiveSecondCount: number,
  awayUnderOneSecondCount: number,
  copyPasteMismatchCount: number,
}

@Injectable({
  providedIn: "root"
})
export class StudentSoftLockService {
  // this service owns auto-pause while legacy softlock stays in the page switchers
  private readonly DEFAULT_SHORT_AWAY_THRESHOLD_SEC = 1;
  private readonly DEFAULT_LONG_AWAY_THRESHOLD_SEC = 5;
  private readonly DEFAULT_SHORT_AWAY_THRESHOLD_COUNT = 10;
  private readonly DEFAULT_SOFTLOCK_CONTROLS: ISoftlockControls = {
    softlock: { autoPause: true, allowSessionDisable: false },
    reEntry: { autoPause: true },
    timeAway: {
      showIndicator: true,
      autoPause: true,
      shortAwayThresholdSec: this.DEFAULT_SHORT_AWAY_THRESHOLD_SEC,
      longAwayThresholdSec: this.DEFAULT_LONG_AWAY_THRESHOLD_SEC,
      shortAwayThresholdCount: this.DEFAULT_SHORT_AWAY_THRESHOLD_COUNT,
    },
    copyPasteMismatch: {
      showIndicator: true,
      autoPause: true,
    },
  };
  private readonly METRICS_STORAGE_KEY = 'eqao.softlock.metrics.v1';
  private readonly DEBUG_STORAGE_KEY = 'SOFTLOCK_DEBUG';

  private ctx: IAttemptContext;
  private awayPauseTimeout: ReturnType<typeof setTimeout> | null = null;
  private shortAwayThresholdMs = this.DEFAULT_SHORT_AWAY_THRESHOLD_SEC * 1000;
  private longAwayThresholdMs = this.DEFAULT_LONG_AWAY_THRESHOLD_SEC * 1000;
  private shortAwayThresholdCount = this.DEFAULT_SHORT_AWAY_THRESHOLD_COUNT;
  private autoPausedForCurrentAway = false;
  private pauseInFlight = false;
  private fullscreenExitPromptActive = false;
  private reentryPauseAttemptedKey: string | null = null;

  public isWindowFocused = true;
  public screenFreezeSub: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public triggerTestRunnerSave$: Subject<void> = new Subject();

  constructor(
    private auth: AuthService,
    private routes: RoutesService,
    private assessmentLock: AssessmentLockService,
    private lang: LangService,
    private loginGuard: LoginGuardService,
    private studentG9Connection: StudentG9ConnectionService,
  ) {
    this.studentG9Connection.studentSoftLockStatusChangedSub.subscribe((status) => {
      this.handleSoftLockStatusChanged(status?.testAttemptId).catch(() => {});
    });
  }

  init(ctx: IAttemptContextInit) {
    const previousMetrics = this.getCurrentMetricsForContext(ctx);
    this.destroy();

    // keep counts when the same attempt rebuilds the runner, like osslt moving between modules
    this.ctx = {
      ...ctx,
      numTimesOffScreen: previousMetrics?.numTimesOffScreen || 0,
      is_paused: !!ctx.is_paused,
      timeAwayStartedOn: null,
      timeAwayTotalMs: previousMetrics?.timeAwayTotalMs || 0,
      timeAwayMostRecentMs: previousMetrics?.timeAwayMostRecentMs || 0,
      awayOverOneSecondCount: previousMetrics?.awayOverOneSecondCount || 0,
      awayOneToFiveSecondCount: previousMetrics?.awayOneToFiveSecondCount || 0,
      awayUnderOneSecondCount: previousMetrics?.awayUnderOneSecondCount || 0,
      copyPasteMismatchCount: previousMetrics?.copyPasteMismatchCount || 0,
    };
    this.applyThresholds();
    if (!previousMetrics) {
      this.hydratePersistedMetrics();
    }
    this.debugLog('init', {
      attemptId: this.ctx.test_attempt_id,
      sessionId: this.ctx.test_session_id,
      softlockMode: this.ctx.softlockMode,
      isSoftLockActive: this.isSoftLockActive(),
      isTimeAwaySoftLockActive: this.isTimeAwaySoftLockActive(),
      isCopyPasteMismatchSoftLockActive: this.isCopyPasteMismatchSoftLockActive(),
      is_paused: this.ctx.is_paused,
      is_soft_lock_disabled: this.ctx.is_soft_lock_disabled,
      usedPreviousMetrics: !!previousMetrics,
    });
    this.sessionEventLog('STUDENT_SOFTLOCK_INIT', {
      usedPreviousMetrics: !!previousMetrics,
    });

    this.startRuntime();
  }

  private startRuntime() {
    if (!this.isSoftLockMonitoringActive()) {
      return;
    }

    // only watch screen changes when time-away counts or time-away pauses are enabled
    if (this.shouldRecordAwayDuration()) {
      if (this.shouldForceFullScreen()) {
        this.forceFullScreen();
      }
      document.removeEventListener('fullscreenchange', this.detectFullScreen, true);
      document.addEventListener('fullscreenchange', this.detectFullScreen, true);
      window.onblur = this.windowBlurred;
      window.onfocus = this.windowFocused;
    }
    this.syncSoftLockStatusOnEntry();
    this.pauseIfPreviouslyOpened();
  }

  private syncSoftLockStatusOnEntry() {
    const attemptId = this.ctx.test_attempt_id;
    // the page payload can be stale after refresh or re-entry, so check the latest server state
    this.recheckStudentSoftLock('entry').then(() => {
      if (!this.ctx || this.ctx.test_attempt_id !== attemptId) {
        return;
      }
      if (!this.isSoftLockMonitoringActive()) {
        this.screenFreezeSub.next(false);
        this.stopRuntime();
        return;
      }
      this.syncTeacherIndicators();
      if (this.ctx.is_paused) {
        this.activatePauseNotif();
      }
    }).catch((_err) => {
      if (!this.ctx || this.ctx.test_attempt_id !== attemptId || !this.isSoftLockMonitoringActive()) {
        return;
      }
      this.syncTeacherIndicators();
      if (this.ctx.is_paused) {
        this.activatePauseNotif();
      }
    });
  }

  private async handleSoftLockStatusChanged(testAttemptId?: number) {
    if (!this.ctx) {
      return;
    }
    const currentAttemptId = Number(this.ctx.test_attempt_id);
    if (Number(testAttemptId) !== currentAttemptId) {
      return;
    }
    // the teacher message is only a nudge, the student endpoint has the real paused and disabled state
    const wasUnpaused = await this.recheckStudentSoftLock('teacher_status_changed');
    if (!this.ctx) {
      return;
    }
    if (!this.ctx.is_paused && (wasUnpaused || this.screenFreezeSub.getValue())) {
      this.screenFreezeSub.next(false);
      this.loginGuard.confirmationReqDeactivate();
      if (wasUnpaused) {
        this.sessionEventLog('STUDENT_SOFTLOCK_UNPAUSED', {
          source: 'teacher_status_changed',
        });
        setTimeout(() => this.loginGuard.quickPopup(this.lang.tra('msg_student_unpaused')), 0);
      }
    }
    if (!this.isSoftLockMonitoringActive()) {
      this.stopRuntime();
      return;
    }
    this.startRuntime();
  }

  destroy() {
    this.stopRuntime();
    this.screenFreezeSub.next(false);
  }

  private stopRuntime() {
    // remove page listeners before the next assessment page creates its own softlock state
    document.removeEventListener('fullscreenchange', this.detectFullScreen, true);
    window.onblur = () => {};
    window.onfocus = () => {};
    this.clearAwayPauseTimeout();
    this.pauseInFlight = false;
    this.fullscreenExitPromptActive = false;
    this.autoPausedForCurrentAway = false;
  }

  isAutoPauseMode() {
    return this.ctx?.softlockMode === SOFTLOCK_MODE_AUTO_PAUSE;
  }

  isSoftLockMonitoringActive() {
    // monitoring can keep updating indicators and logs when a teacher disables auto pausing
    return !!this.ctx
      && this.isAutoPauseMode()
      && this.ctx.isSebMode === false;
  }

  isSoftLockActive() {
    // this remains the gate for actually pausing the student
    return this.isSoftLockMonitoringActive()
      && Number(this.ctx.is_soft_lock_disabled || 0) === 0;
  }

  isTimeAwaySoftLockActive() {
    return this.isSoftLockActive()
      && this.getSoftlockControls().timeAway.autoPause !== false;
  }

  isReEntrySoftLockActive() {
    return this.isSoftLockActive()
      && this.getSoftlockControls().reEntry.autoPause !== false;
  }

  shouldRecordAwayDuration() {
    // counts can still be shown to teachers even when time-away pausing is turned off
    return this.isSoftLockMonitoringActive()
      && (
        this.getSoftlockControls().timeAway.showIndicator !== false
        || this.isTimeAwaySoftLockActive()
      );
  }

  isCopyPasteMismatchSoftLockActive() {
    return this.isSoftLockActive()
      && this.getSoftlockControls().copyPasteMismatch.autoPause !== false;
  }

  shouldRecordPasteMismatch() {
    // paste mismatches can still be shown to teachers even when paste pausing is turned off
    return this.isSoftLockMonitoringActive()
      && (
        this.getSoftlockControls().copyPasteMismatch.showIndicator !== false
        || this.isCopyPasteMismatchSoftLockActive()
      );
  }

  beforeSaveCheck = async () => {
    if (!this.ctx || !this.isAutoPauseMode()) {
      return true;
    }

    try {
      await this.recheckStudentSoftLock();
    }
    catch (_err) {
      // softlock status checks should not be the reason an answer fails to save
      return true;
    }

    if (this.ctx.is_paused && this.isSoftLockActive()) {
      this.sessionEventLog('STUDENT_SOFTLOCK_BEFORE_SAVE_BLOCKED');
      this.activatePauseNotif();
      return false;
    }
    return true;
  }

  public handleAttemptPaused() {
    if (!this.isAutoPauseMode()) {
      return false;
    }
    if (this.ctx) {
      this.ctx.is_paused = true;
    }
    this.activatePauseNotif();
    return true;
  }

  private pauseIfPreviouslyOpened() {
    if (!this.ctx?.isPreviouslyOpened || !this.isReEntrySoftLockActive()) {
      return;
    }
    const reentryKey = [
      this.ctx.uid,
      this.ctx.test_session_id,
      this.ctx.test_attempt_id,
      this.ctx.currentStudentPosition?.subSessionSlug || '',
    ].join(':');
    if (this.reentryPauseAttemptedKey === reentryKey) {
      return;
    }
    this.reentryPauseAttemptedKey = reentryKey;
    // normal module movement should arrive as a first open for the active subsession
    this.sessionEventLog('STUDENT_SOFTLOCK_REENTRY_DETECTED', {
      reentryKey,
    });
    this.pauseAndLockStudent('REENTRY');
  }

  triggerPasteMismatchAutoPause = async (mismatchEvent: IPasteMismatchEvent) => {
    if (!this.shouldRecordPasteMismatch()) {
      this.sessionEventLog('STUDENT_SOFTLOCK_PASTE_MISMATCH_SKIPPED', {
        skipReason: 'paste_mismatch_not_active',
        pasteMismatch: this.getPasteMismatchLogDetail(mismatchEvent),
      });
      return;
    }
    mismatchEvent.subSessionSlug = mismatchEvent.subSessionSlug || this.ctx.currentStudentPosition.subSessionSlug || null;
    mismatchEvent.subSessionCaption = mismatchEvent.subSessionCaption || this.ctx.currentStudentPosition.subSessionCaption || null;
    mismatchEvent.questionCaption = mismatchEvent.questionCaption || this.ctx.currentStudentPosition.questionCaption || null;
    this.ctx.copyPasteMismatchCount++;
    this.persistMetrics();
    this.sessionEventLog('STUDENT_SOFTLOCK_PASTE_MISMATCH_DETECTED', {
      willAutoPause: this.isCopyPasteMismatchSoftLockActive(),
      pasteMismatch: this.getPasteMismatchLogDetail(mismatchEvent),
    });
    // send the count before pausing so teachers still see the mismatch if the pause request fails
    this.setStudentSoftLock(null, 'PASTE_MISMATCH', false, {
      pasteMismatchEvent: mismatchEvent,
      copyPasteMismatchCount: this.ctx.copyPasteMismatchCount,
    });
    if (this.isCopyPasteMismatchSoftLockActive()) {
      await this.pauseAndLockStudent('PASTE_MISMATCH');
    }
  }

  private applyThresholds() {
    // long-away cannot be shorter than short-away, even if the saved config is wrong
    const controls = this.getSoftlockControls();
    this.shortAwayThresholdMs = Math.max(0, Number(controls.timeAway.shortAwayThresholdSec ?? this.DEFAULT_SHORT_AWAY_THRESHOLD_SEC) * 1000);
    this.longAwayThresholdMs = Math.max(this.shortAwayThresholdMs, Number(controls.timeAway.longAwayThresholdSec ?? this.DEFAULT_LONG_AWAY_THRESHOLD_SEC) * 1000);
    this.shortAwayThresholdCount = Math.max(1, Number(controls.timeAway.shortAwayThresholdCount ?? this.DEFAULT_SHORT_AWAY_THRESHOLD_COUNT));
  }

  private async recheckStudentSoftLock(logReason?: string) {
    if (!this.ctx) {
      return false;
    }
    const status = await this.auth.apiGet(this.routes.STUDENT_SOFT_LOCK, this.ctx.test_attempt_id);
    if (status) {
      const wasPaused = this.ctx.is_paused;
      this.ctx.is_paused = !!status.is_paused;
      this.ctx.is_soft_lock_disabled = status.is_soft_lock_disabled;
      this.ctx.softlockMode = status.softlock_mode || this.ctx.softlockMode;
      this.ctx.softlockControls = status.softlockControls || this.ctx.softlockControls;
      this.applyThresholds();
      this.debugLog('recheckStudentSoftLock', {
        attemptId: this.ctx.test_attempt_id,
        wasPaused,
        apiIsPaused: status.is_paused,
        localIsPaused: this.ctx.is_paused,
        apiSoftLockDisabled: status.is_soft_lock_disabled,
      });
      if (logReason) {
        this.sessionEventLog('STUDENT_SOFTLOCK_STATUS_RECHECK', {
          logReason,
          wasPaused,
          apiIsPaused: status.is_paused,
          apiSoftLockDisabled: status.is_soft_lock_disabled,
          apiSoftlockMode: status.softlock_mode,
        });
      }
      return wasPaused && !this.ctx.is_paused;
    }
    return false;
  }

  async pauseAndLockStudent(pauseCause: SoftLockPauseCause, extraPayload?: Partial<IStudentPositionUpdate>) {
    if (this.pauseInFlight) {
      this.sessionEventLog('STUDENT_SOFTLOCK_AUTO_PAUSE_SKIPPED', {
        pauseCause,
        skipReason: 'pause_in_flight',
      });
      return;
    }
    this.pauseInFlight = true;
    try {
      await this.recheckStudentSoftLock('before_auto_pause');

      if (!this.isSoftLockActive() || this.ctx.is_paused) {
        this.sessionEventLog('STUDENT_SOFTLOCK_AUTO_PAUSE_SKIPPED', {
          pauseCause,
          skipReason: this.ctx.is_paused ? 'already_paused' : 'softlock_not_active',
        });
        return;
      }
      // ask the runner to save before we mark the attempt paused
      this.triggerTestRunnerSave$.next();

      // student auto-pauses use the student endpoint, teacher actions still use the educator endpoint
      this.debugLog('pauseAndLockStudent:beforePatch', {
        attemptId: this.ctx.test_attempt_id,
        pauseCause,
        is_paused: this.ctx.is_paused,
      });
      this.sessionEventLog('STUDENT_SOFTLOCK_AUTO_PAUSE_TRIGGERED', {
        pauseCause,
      });
      let result;
      try {
        result = await this.auth.apiPatch(this.routes.STUDENT_SOFT_LOCK, this.ctx.test_attempt_id, { pauseCause });
      } catch (err) {
        this.sessionEventLog('STUDENT_SOFTLOCK_AUTO_PAUSE_FAILED', {
          pauseCause,
          error: this.getLoggableError(err),
        });
        throw err;
      }
      if (result) {
        this.ctx.is_paused = !!result.is_paused;
        this.ctx.is_soft_lock_disabled = result.is_soft_lock_disabled;
        this.ctx.softlockMode = result.softlock_mode || this.ctx.softlockMode;
        this.ctx.softlockControls = result.softlockControls || this.ctx.softlockControls;
        this.applyThresholds();
      }
      if (!this.ctx.is_paused) {
        // the api can skip the pause if the teacher disabled softlock while this request was in flight
        this.sessionEventLog('STUDENT_SOFTLOCK_AUTO_PAUSE_SKIPPED', {
          pauseCause,
          skipReason: result?.pauseApplied === false ? 'api_skipped_pause' : 'api_returned_unpaused',
          apiIsPaused: result?.is_paused,
          apiSoftLockDisabled: result?.is_soft_lock_disabled,
        });
        if (!this.isSoftLockMonitoringActive()) {
          this.stopRuntime();
        }
        return;
      }
      this.sessionEventLog('STUDENT_SOFTLOCK_AUTO_PAUSE_SUCCEEDED', {
        pauseCause,
      });
      this.setStudentSoftLock(null, pauseCause, true, extraPayload);
      this.activatePauseNotif();
    }
    finally {
      this.pauseInFlight = false;
    }
  }

  async forceFullScreen() {
    // safari and ipad stay out of forced fullscreen because typing can look like a fullscreen exit
    if (this.isSafari() || this.isiPad() || !this.isTimeAwaySoftLockActive()) {
      return;
    }
    const elem = document.documentElement;
    const methodToBeInvoked = elem.requestFullscreen || elem['mozRequestFullscreen'] || elem['msRequestFullscreen'];
    if (methodToBeInvoked) {
      try {
        await methodToBeInvoked.call(elem);
      } catch (_err) {
        this.setStudentSoftLock(null, 'FORCE_FULLSCREEN_ERR');
      }
    }
  }

  detectFullScreen = () => {
    if (!this.isTimeAwaySoftLockActive() || this.isAssessmentLockFullscreenOwner()) {
      return;
    }
    if (!document.fullscreenElement && !this.fullscreenExitPromptActive) {
      this.fullscreenExitPromptActive = true;
      this.loginGuard.confirmationReqActivate({
        caption: this.lang.tra('msg_student_leave_fullscreen_popup'),
        btnCancelConfig: {
          caption: 'msg_student_return_to_fullscreen'
        },
        btnProceedConfig: {
          caption: 'msg_student_exit_fullscreen'
        },
        confirm: () => {
          this.fullscreenExitPromptActive = false;
          if (this.ctx?.is_paused) {
            // keep the paused message visible if another modal was opened on top of it
            setTimeout(() => this.activatePauseNotif());
            return;
          }
          this.pauseAndLockStudent('LEAVE_FULLSCREEN_CONFIRM');
        },
        close: () => {
          this.fullscreenExitPromptActive = false;
          if (this.shouldForceFullScreen()) {
            this.forceFullScreen();
          }
          setTimeout(() => {
            if (this.ctx?.is_paused) {
              // return to the paused message after the fullscreen prompt closes
              this.activatePauseNotif();
            }
          }, 0);
        },
      });
    }
  }

  windowFocused = (event?) => {
    this.isWindowFocused = true;
    if (!this.shouldRecordAwayDuration() || this.ctx.timeAwayStartedOn == null) {
      return;
    }

    const durationMs = Math.max(0, Date.now() - this.ctx.timeAwayStartedOn);
    // the total keeps growing, while the latest value is used for the threshold check
    this.ctx.timeAwayMostRecentMs = durationMs;
    this.ctx.timeAwayTotalMs += durationMs;
    if (durationMs >= this.longAwayThresholdMs) {
      this.ctx.awayOverOneSecondCount++;
    } else if (durationMs >= this.shortAwayThresholdMs) {
      this.ctx.awayOverOneSecondCount++;
      this.ctx.awayOneToFiveSecondCount++;
    } else {
      this.ctx.awayUnderOneSecondCount++;
    }
    this.ctx.timeAwayStartedOn = null;
    this.clearAwayPauseTimeout();
    this.persistMetrics();
    this.setStudentSoftLock(event, 'WINDOW_FOCUS_DETECTED', false);
    this.evaluateTimeAwayAutoPauseThresholds();
    this.autoPausedForCurrentAway = false;
  }

  windowBlurred = (event?) => {
    if (!this.shouldRecordAwayDuration()) {
      return;
    }
    this.isWindowFocused = false;
    const target = event?.target;
    if (target?.length && target[0].frameElement?.id === CALC_FRAME_ID) {
      target[0].onblur = this.windowBlurred;
      target[0].onfocus = this.windowFocused;
    }

    setTimeout(() => {
      // ignore blur events that immediately bounce back, like focus moving inside browser controls
      if (this.isWindowFocused || !this.shouldRecordAwayDuration()) {
        return;
      }
      this.ctx.numTimesOffScreen++;
      if (this.ctx.timeAwayStartedOn == null) {
        this.ctx.timeAwayStartedOn = Date.now();
        this.autoPausedForCurrentAway = false;
        if (this.isTimeAwaySoftLockActive()) {
          this.startAwayPauseTimer();
        }
      }
      this.persistMetrics();
      this.setStudentSoftLock(event, 'WINDOW_BLUR_DETECTED', false);
    }, 5);
  }

  setStudentSoftLock = (event?, pauseCause?: SoftLockPauseCause, pauseIntent = false, extraPayload?: Partial<IStudentPositionUpdate>) => {
    if (!this.isSoftLockMonitoringActive()) {
      return;
    }
    // this sends the counts and pause state that the teacher row needs to update live
    const payload: Partial<IStudentPositionUpdate> = {
      stageIndex: this.ctx.currentStudentPosition.stageIndex,
      questionCaption: this.ctx.currentStudentPosition.questionCaption,
      softLock: 1,
      numTimesOffScreen: this.ctx.numTimesOffScreen,
      softlockMode: this.ctx.softlockMode,
      softlockControls: this.getSoftlockControls(),
      is_paused: this.ctx.is_paused,
      awayOverOneSecondCount: this.ctx.awayOverOneSecondCount,
      awayOneToFiveSecondCount: this.ctx.awayOneToFiveSecondCount,
      awayUnderOneSecondCount: this.ctx.awayUnderOneSecondCount,
      copyPasteMismatchCount: this.ctx.copyPasteMismatchCount,
      indicatorCountUpdateOnly: pauseCause === 'WINDOW_BLUR_DETECTED' || pauseCause === 'WINDOW_FOCUS_DETECTED',
      pauseIntent,
      pauseCause,
      ...extraPayload,
    };
    this.debugLog('setStudentSoftLock', payload);
    this.studentG9Connection.updateStudentPosition(payload).catch(() => {});
  }

  private syncTeacherIndicators() {
    this.setStudentSoftLock(null, undefined, false);
  }

  private startAwayPauseTimer() {
    this.clearAwayPauseTimeout();
    this.awayPauseTimeout = setTimeout(() => {
      if (!this.ctx || this.ctx.timeAwayStartedOn == null || this.isWindowFocused || !this.isTimeAwaySoftLockActive()) {
        return;
      }
      // this catches a single long away event before the student comes back to the page
      this.autoPausedForCurrentAway = true;
      this.pauseAndLockStudent('THRESHOLD_LONG_AWAY');
    }, this.longAwayThresholdMs);
  }

  private clearAwayPauseTimeout() {
    if (this.awayPauseTimeout) {
      clearTimeout(this.awayPauseTimeout);
      this.awayPauseTimeout = null;
    }
  }

  private evaluateTimeAwayAutoPauseThresholds() {
    if (!this.ctx || this.ctx.is_paused || this.autoPausedForCurrentAway || !this.isTimeAwaySoftLockActive()) {
      return;
    }
    if (this.ctx.timeAwayMostRecentMs > this.longAwayThresholdMs) {
      this.pauseAndLockStudent('THRESHOLD_LONG_AWAY');
      return;
    }
    const shortAwayCount = this.ctx.awayUnderOneSecondCount + this.ctx.awayOneToFiveSecondCount;
    // pause again each time the student reaches another full short-away group
    if (shortAwayCount >= this.shortAwayThresholdCount && shortAwayCount % this.shortAwayThresholdCount === 0) {
      this.pauseAndLockStudent('THRESHOLD_SHORT_AWAY_COUNT');
    }
  }

  private activatePauseNotif() {
    // screen freeze blocks the runner, the retry modal lets the student check if the teacher unpaused them
    this.screenFreezeSub.next(true);
    this.loginGuard.confirmationReqActivate({
      caption: `${this.lang.tra('msg_cannot_proceed_test')} ${this.lang.tra('msg_session_pause')}`,
      btnCancelConfig: { hide: true },
      btnProceedConfig: { caption: 'btn_retry' },
      confirm: () => this.onSoftLockRetry(),
    });
  }

  public async onSoftLockRetry() {
    try {
      const wasUnpaused = await this.recheckStudentSoftLock();
      if (this.ctx?.is_paused) {
        // login guard closes the current modal after confirm, so reopen after that close finishes
        setTimeout(() => this.activatePauseNotif());
        return;
      }
      this.fullscreenExitPromptActive = false;
      this.screenFreezeSub.next(false);
      this.loginGuard.confirmationReqDeactivate();
      if (this.shouldForceFullScreen()) {
        this.forceFullScreen();
      }
      if (wasUnpaused) {
        // show this after the paused modal closes so it is not cleared too early
        this.sessionEventLog('STUDENT_SOFTLOCK_UNPAUSED', {
          source: 'retry',
        });
        setTimeout(() => this.loginGuard.quickPopup(this.lang.tra('msg_student_unpaused')), 0);
      }
    } catch (_err) {
      // keep the student blocked if the retry status check fails
      setTimeout(() => this.activatePauseNotif());
    }
  }

  private shouldForceFullScreen() {
    // assessment lock owns fullscreen once active, so this service should not fight it
    return this.isTimeAwaySoftLockActive()
      && !this.auth.activeKioskPassword
      && !this.isAssessmentLockFullscreenOwner();
  }

  private getSoftlockControls(): ISoftlockControls {
    // merge saved test ctrl values with defaults so older windows still behave predictably
    return {
      softlock: {
        ...this.DEFAULT_SOFTLOCK_CONTROLS.softlock,
        ...(this.ctx?.softlockControls?.softlock || {}),
      },
      reEntry: {
        ...this.DEFAULT_SOFTLOCK_CONTROLS.reEntry,
        ...(this.ctx?.softlockControls?.reEntry || {}),
      },
      timeAway: {
        ...this.DEFAULT_SOFTLOCK_CONTROLS.timeAway,
        ...(this.ctx?.softlockControls?.timeAway || {}),
      },
      copyPasteMismatch: {
        ...this.DEFAULT_SOFTLOCK_CONTROLS.copyPasteMismatch,
        ...(this.ctx?.softlockControls?.copyPasteMismatch || {}),
      },
    };
  }

  private isAssessmentLockFullscreenOwner() {
    return this.assessmentLock.isActive || this.assessmentLock.currentState.isLockdown;
  }

  private getCurrentMetricsForContext(ctx: IAttemptContextInit): IPersistedSoftLockMetrics | null {
    if (!this.ctx
      || this.ctx.uid !== ctx.uid
      || this.ctx.test_attempt_id !== ctx.test_attempt_id
      || this.ctx.test_session_id !== ctx.test_session_id) {
      return null;
    }
    // keep in-memory counts when the same student attempt rebuilds the runner
    return {
      uid: this.ctx.uid,
      test_attempt_id: this.ctx.test_attempt_id,
      test_session_id: this.ctx.test_session_id,
      numTimesOffScreen: this.ctx.numTimesOffScreen,
      timeAwayTotalMs: this.ctx.timeAwayTotalMs,
      timeAwayMostRecentMs: this.ctx.timeAwayMostRecentMs,
      awayOverOneSecondCount: this.ctx.awayOverOneSecondCount,
      awayOneToFiveSecondCount: this.ctx.awayOneToFiveSecondCount,
      awayUnderOneSecondCount: this.ctx.awayUnderOneSecondCount,
      copyPasteMismatchCount: this.ctx.copyPasteMismatchCount,
    };
  }

  private sessionEventLog(slug: string, data: any = {}) {
    if (!this.ctx) {
      return;
    }

    // public/log records the student-side decision path
    this.auth.apiCreate(this.routes.LOG, {
      slug,
      data: this.getSoftLockLogContext(data),
    }).catch(() => {});
  }

  private getSoftLockLogContext(data: any = {}) {
    return {
      uid: this.ctx.uid,
      test_attempt_id: this.ctx.test_attempt_id,
      test_session_id: this.ctx.test_session_id,
      softlockMode: this.ctx.softlockMode,
      is_paused: this.ctx.is_paused,
      is_soft_lock_disabled: this.ctx.is_soft_lock_disabled,
      isSoftLockActive: this.isSoftLockActive(),
      isTimeAwaySoftLockActive: this.isTimeAwaySoftLockActive(),
      isReEntrySoftLockActive: this.isReEntrySoftLockActive(),
      isCopyPasteMismatchSoftLockActive: this.isCopyPasteMismatchSoftLockActive(),
      isPreviouslyOpened: this.ctx.isPreviouslyOpened,
      currentStudentPosition: this.ctx.currentStudentPosition,
      numTimesOffScreen: this.ctx.numTimesOffScreen,
      timeAwayMostRecentMs: this.ctx.timeAwayMostRecentMs,
      timeAwayTotalMs: this.ctx.timeAwayTotalMs,
      awayOverOneSecondCount: this.ctx.awayOverOneSecondCount,
      awayOneToFiveSecondCount: this.ctx.awayOneToFiveSecondCount,
      awayUnderOneSecondCount: this.ctx.awayUnderOneSecondCount,
      copyPasteMismatchCount: this.ctx.copyPasteMismatchCount,
      thresholds: {
        shortAwayThresholdSec: this.shortAwayThresholdMs / 1000,
        longAwayThresholdSec: this.longAwayThresholdMs / 1000,
        shortAwayThresholdCount: this.shortAwayThresholdCount,
      },
      ...data,
    };
  }

  private getPasteMismatchLogDetail(event?: IPasteMismatchEvent) {
    if (!event) {
      return null;
    }

    return {
      type: event.type,
      source: event.source,
      localTimestamp: event.timestamp,
      targetType: event.targetType,
      xPath: event.xPath,
      copiedCacheSize: event.copiedCacheSize,
      pastedText: event.text,
      pastedTextLength: typeof event.text === 'string' ? event.text.length : 0,
      subSessionId: event.subSessionId,
      questionId: event.questionId,
      questionCaption: event.questionCaption,
    };
  }

  private getLoggableError(err: any) {
    return {
      name: err?.name,
      message: err?.message,
      code: err?.code,
      className: err?.className,
    };
  }

  private debugLog(message: string, payload?: unknown) {
    if (localStorage.getItem(this.DEBUG_STORAGE_KEY) !== '1') {
      return;
    }
    console.log(`[softlock] ${message}`, payload);
  }

  private hydratePersistedMetrics() {
    if (!this.ctx) {
      return;
    }
    try {
      const raw = localStorage.getItem(this.METRICS_STORAGE_KEY);
      if (!raw) {
        return;
      }
      const stored: IPersistedSoftLockMetrics = JSON.parse(raw);
      if (!stored || stored.uid !== this.ctx.uid || stored.test_attempt_id !== this.ctx.test_attempt_id || stored.test_session_id !== this.ctx.test_session_id) {
        this.persistMetrics();
        return;
      }
      this.ctx.numTimesOffScreen = Number(stored.numTimesOffScreen) || 0;
      this.ctx.timeAwayTotalMs = Number(stored.timeAwayTotalMs) || 0;
      this.ctx.timeAwayMostRecentMs = Number(stored.timeAwayMostRecentMs) || 0;
      this.ctx.awayOverOneSecondCount = Number(stored.awayOverOneSecondCount) || 0;
      this.ctx.awayOneToFiveSecondCount = Number(stored.awayOneToFiveSecondCount) || 0;
      this.ctx.awayUnderOneSecondCount = Number(stored.awayUnderOneSecondCount) || 0;
      this.ctx.copyPasteMismatchCount = Number(stored.copyPasteMismatchCount) || 0;
    } catch (_e) {
      this.persistMetrics();
    }
  }

  private persistMetrics() {
    if (!this.ctx) {
      return;
    }
    const payload: IPersistedSoftLockMetrics = {
      uid: this.ctx.uid,
      test_attempt_id: this.ctx.test_attempt_id,
      test_session_id: this.ctx.test_session_id,
      numTimesOffScreen: this.ctx.numTimesOffScreen,
      timeAwayTotalMs: this.ctx.timeAwayTotalMs,
      timeAwayMostRecentMs: this.ctx.timeAwayMostRecentMs,
      awayOverOneSecondCount: this.ctx.awayOverOneSecondCount,
      awayOneToFiveSecondCount: this.ctx.awayOneToFiveSecondCount,
      awayUnderOneSecondCount: this.ctx.awayUnderOneSecondCount,
      copyPasteMismatchCount: this.ctx.copyPasteMismatchCount,
    };
    try {
      localStorage.setItem(this.METRICS_STORAGE_KEY, JSON.stringify(payload));
    } catch (_e) {}
  }

  private isSafari() {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  private isiPad() {
    return /iPad/i.test(navigator.userAgent);
  }
}